import React, { useState } from 'react';
import { CollaborationItem } from '../types';
import {
  UserIcon,
  PlusIcon,
  XIcon,
} from './DoctoralIcons';

interface DoctoralJointScienceOverlayProps {
  collaborations?: CollaborationItem[];
  onClose: () => void;
  onAddCollaboration?: (item: { kind: string; title: string; description: string }) => void;
}

export const DoctoralJointScienceOverlay: React.FC<DoctoralJointScienceOverlayProps> = ({
  collaborations,
  onClose,
  onAddCollaboration,
}) => {
  const [activeTab, setActiveTab] = useState<'ALL' | 'ARTICLE' | 'REVIEW' | 'MEETING'>('ALL');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newKind, setNewKind] = useState('ARTICLE');
  const [newDesc, setNewDesc] = useState('');

  const defaultCollabs: CollaborationItem[] = [
    {
      id: 'col-1',
      kind: 'ARTICLE',
      title: 'Подготовка совместной статьи для журнала Scopus Q1 (Language & Education)',
      code: 'JOINT-PUB-01',
      description: 'Соавторство: проф. Садыков Б.Т. (отечественный консультант) и Prof. Michael Weber (Берлин).',
      status: 'Черновик рукописи передан на вычитку',
    },
    {
      id: 'col-2',
      kind: 'REVIEW',
      title: 'Рецензирование 2-й главы диссертации «Эмпирические модели обучения»',
      code: 'REV-CH2',
      description: 'Проверка отечественным консультантом методической части исследования.',
      status: 'Замечания устранены, получена положительная виза',
    },
    {
      id: 'col-3',
      kind: 'MEETING',
      title: 'Научный онлайн-семинар с зарубежным консультантом',
      code: 'SEM-BERLIN',
      description: 'Обсуждение промежуточных результатов контент-анализа и планирование дат стажировки.',
      status: 'Состоялся 14.02.2025 г.',
    },
    {
      id: 'col-4',
      kind: 'ARTICLE',
      title: 'Доклад на Международный тюркологический конгресс (Анкара)',
      code: 'CONF-ANKARA',
      description: 'Совместные тезисы с коллективом кафедры казахского языкознания.',
      status: 'Принято к публикации в сборнике',
    },
  ];

  const items = collaborations && collaborations.length > 0 ? collaborations : defaultCollabs;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    if (onAddCollaboration) {
      onAddCollaboration({
        kind: newKind,
        title: newTitle.trim(),
        description: newDesc.trim() || 'Совместная академическая инициатива',
      });
    }

    setNewTitle('');
    setNewDesc('');
    setShowAddForm(false);
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
              <UserIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">
                  Совместная научная работа
                </h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30">
                  Траектория · 02.10.09
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Единая среда взаимодействия: докторант, научный руководитель и зарубежный консультант
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-3 py-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 transition shadow-md shadow-cyan-500/20"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Новая инициатива
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {showAddForm && (
            <form
              onSubmit={handleSubmit}
              className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-3"
            >
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                Предложить совместную статью, рецензию или тему встречи
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-slate-400 text-xs mb-1">Название / Тема</label>
                  <input
                    type="text"
                    required
                    placeholder="Например: Совместная публикация в журнале..."
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-xs mb-1">Тип сотрудничества</label>
                  <select
                    value={newKind}
                    onChange={(e) => setNewKind(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white"
                  >
                    <option value="ARTICLE">Совместная статья</option>
                    <option value="REVIEW">Рецензирование текста</option>
                    <option value="MEETING">Научная встреча / Консультация</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-xs mb-1">Описание и комментарий</label>
                <textarea
                  rows={2}
                  placeholder="Укажите авторов, целевой журнал или вопросы для обсуждения..."
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-slate-200"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-lg bg-cyan-500 text-slate-950 font-bold text-xs hover:bg-cyan-400 transition"
                >
                  Отправить в кабинет руководителя
                </button>
              </div>
            </form>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setActiveTab('ALL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                activeTab === 'ALL'
                  ? 'bg-cyan-500 text-slate-950'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Все инициативы
            </button>
            <button
              onClick={() => setActiveTab('ARTICLE')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                activeTab === 'ARTICLE'
                  ? 'bg-cyan-500 text-slate-950'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Совместные статьи
            </button>
            <button
              onClick={() => setActiveTab('REVIEW')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                activeTab === 'REVIEW'
                  ? 'bg-cyan-500 text-slate-950'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Рецензии
            </button>
            <button
              onClick={() => setActiveTab('MEETING')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                activeTab === 'MEETING'
                  ? 'bg-cyan-500 text-slate-950'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Консультации
            </button>
          </div>

          <div className="space-y-3">
            {items
              .filter((it) => activeTab === 'ALL' || it.kind === activeTab)
              .map((item) => (
                <div
                  key={item.id}
                  className="p-5 rounded-2xl bg-slate-950/60 border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-slate-700 transition"
                >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-bold text-cyan-400 bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                      {item.code}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      {item.kind === 'ARTICLE'
                        ? 'Научная публикация'
                        : item.kind === 'REVIEW'
                        ? 'Рецензия'
                        : 'Консультация'}
                    </span>
                  </div>

                  <h4 className="text-sm font-bold text-white">{item.title}</h4>
                  <p className="text-xs text-slate-400">{item.description}</p>
                </div>

                <div className="flex items-center gap-2 self-start md:self-center">
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-800 text-slate-200 border border-slate-700">
                    {item.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
